import { useGetSystemSettingsQuery, useUpdateSystemSettingsMutation } from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { NumberInput } from '../../components/ui/number-input';
import { Switch } from '../../components/ui/switch';

export function SystemSettingsTab() {
	const { data, loading, refetch } = useGetSystemSettingsQuery({ fetchPolicy: 'no-cache' });
	const [updateSettings, { loading: saving }] = useUpdateSystemSettingsMutation();

	const [defaultApproval, setDefaultApproval] = useState(false);
	const [defaultMargin, setDefaultMargin] = useState('');

	const settings = data?.getSystemSettings;

	useEffect(() => {
		if (settings) {
			setDefaultApproval(settings.defaultApproval);
			setDefaultMargin(String(settings.defaultMargin));
		}
	}, [settings]);

	const hasChanges =
		settings &&
		(defaultApproval !== settings.defaultApproval ||
			defaultMargin !== String(settings.defaultMargin));

	const handleSave = async () => {
		try {
			await updateSettings({
				variables: {
					input: {
						defaultApproval,
						defaultMargin: parseFloat(defaultMargin) || 0,
					},
				},
			});
			toast.success('Settings updated');
			refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update settings');
		}
	};

	if (loading) {
		return (
			<Card className="border-line-1 bg-surface gap-0 py-0">
				<div className="space-y-4 p-6">
					<div className="h-5 w-40 animate-pulse rounded bg-surface-fill" />
					<div className="h-8 w-full animate-pulse rounded bg-surface-fill" />
					<div className="h-5 w-40 animate-pulse rounded bg-surface-fill" />
					<div className="h-8 w-full animate-pulse rounded bg-surface-fill" />
				</div>
			</Card>
		);
	}

	return (
		<Card className="border-line-1 bg-surface gap-0 py-0">
			<div className="flex items-center justify-between border-b border-line-1 px-6 py-3">
				<h3 className="text-sm font-medium uppercase tracking-[0.04em] text-text-1">
					System settings
				</h3>
			</div>
			<div className="flex flex-col gap-6 p-6">
				{/* Default approval */}
				<div className="flex items-center justify-between rounded-lg border border-line-2 px-4 py-3">
					<div>
						<Label className="text-sm">Default approval</Label>
						<p className="text-sm text-muted-foreground">
							Enable default approval for new organizations
						</p>
					</div>
					<Switch checked={defaultApproval} onCheckedChange={setDefaultApproval} />
				</div>

				{/* Default margin */}
				<div className="flex flex-col gap-1.5">
					<Label>Default margin (%)</Label>
					<p className="text-sm text-muted-foreground">
						Default profit margin percentage for new organizations
					</p>
					<NumberInput
						value={defaultMargin}
						onChange={(e) => setDefaultMargin(e.target.value)}
						placeholder="0"
						min={0}
						max={100}
						step={0.01}
						className="max-w-[200px]"
					/>
				</div>

				{/* Save */}
				<div className="flex justify-end border-t border-line-1 pt-4">
					<Button onClick={handleSave} disabled={!hasChanges || saving}>
						{saving ? 'Saving…' : 'Save changes'}
					</Button>
				</div>
			</div>
		</Card>
	);
}
